package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.api.IdeMetadata
import top.wcpe.allincore.dsl.compiler.api.PropMetadata
import top.wcpe.allincore.dsl.compiler.ast.ElementAst
import top.wcpe.allincore.dsl.compiler.ast.ElementWalk
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.ScreenAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.type.IrType
import top.wcpe.allincore.dsl.compiler.type.TypeInferencer
import top.wcpe.allincore.dsl.compiler.type.TypeResolver

/** FR-UI-066 modelPreview 专有编译期契约。 */
internal class ModelPreviewChecker(
    private val diag: DiagnosticCollector,
) {
    private val stateTypes = linkedMapOf<String, IrType>()
    private val sessionTypes = linkedMapOf<String, IrType>()
    private val persistTypes = linkedMapOf<String, IrType>()

    fun check(screen: ScreenAst) {
        collectTypes(screen)
        checkElement(screen.root)
    }

    private fun collectTypes(screen: ScreenAst) {
        stateTypes.clear()
        sessionTypes.clear()
        persistTypes.clear()
        screen.state?.entries?.forEach { entry ->
            entry.type?.let(TypeResolver::resolve)?.let { stateTypes[entry.name] = it }
        }
        screen.sessionState?.entries?.forEach { entry ->
            entry.type?.let(TypeResolver::resolve)?.let { sessionTypes[entry.name] = it }
        }
        screen.persistState?.entries?.forEach { entry ->
            entry.type?.let(TypeResolver::resolve)?.let { persistTypes[entry.name] = it }
        }
    }

    private fun checkElement(element: ElementAst) {
        if (element.kind == MODEL_PREVIEW_KIND) {
            checkModelPreview(element)
        }
        ElementWalk.directDescendants(element).forEach(::checkElement)
        element.menuBlocks.forEach { checkElement(it.root) }
    }

    private fun checkModelPreview(element: ElementAst) {
        checkPropNames(element)
        val props = element.props.associateBy { it.name }
        if ("source" !in props) {
            diag.error(DslError.E407_MODEL_PREVIEW_SOURCE_REQUIRED, element.position, element.id)
        }
        props.values.forEach(::checkProp)
    }

    private fun checkPropNames(element: ElementAst) {
        for (prop in element.props) {
            when {
                prop.name == IdeMetadata.modelPreviewBaseProp ->
                    diag.error(DslError.E413_MODEL_PREVIEW_BASE_RESERVED, prop.position, prop.name)
                prop.name !in ALLOWED_PROPS ->
                    diag.error(DslError.E406_MODEL_PREVIEW_UNKNOWN_PROP, prop.position, prop.name)
            }
        }
    }

    private fun checkProp(prop: ElementAst.PropAssign) {
        when (prop.name) {
            "source" -> checkStringProp(prop, nullable = false, pathCheck = ::checkSourcePath)
            "texture" -> checkStringProp(prop, nullable = true, pathCheck = ::checkTexturePath)
            "scale" -> checkNumberProp(prop, ::checkScale)
            "rotationX", "rotationY" -> checkNumberProp(prop) { value, position ->
                checkRotation(prop.name, value, position)
            }
            "interactive" -> checkType(prop, IrType.BooleanT, "Boolean")
            "backgroundColor" -> checkNullableStringProp(prop, "Color?")
        }
    }

    private fun checkStringProp(
        prop: ElementAst.PropAssign,
        nullable: Boolean,
        pathCheck: (ExprAst.Literal.StringLit) -> Unit,
    ) {
        if (nullable && prop.value is ExprAst.NullLit) return
        val expectedType = if (nullable) IrType.Nullable(IrType.StringT) else IrType.StringT
        if (!checkType(prop, expectedType, if (nullable) "String?" else "String")) return
        (prop.value as? ExprAst.Literal.StringLit)?.let(pathCheck)
    }

    private fun checkNullableStringProp(prop: ElementAst.PropAssign, expectedName: String) {
        if (prop.value is ExprAst.NullLit) return
        checkType(prop, IrType.Nullable(IrType.StringT), expectedName)
    }

    private fun checkNumberProp(
        prop: ElementAst.PropAssign,
        literalCheck: (Double, top.wcpe.allincore.dsl.compiler.source.SourcePosition) -> Unit,
    ) {
        if (!checkType(prop, IrType.NumberT, "Number")) return
        val value = (prop.value as? ExprAst.Literal.NumberLit)?.value?.toDouble() ?: return
        literalCheck(value, prop.value.position)
    }

    private fun checkType(prop: ElementAst.PropAssign, expected: IrType, expectedName: String): Boolean {
        val actual = knownType(prop.value)
        if (actual.isAssignableTo(expected)) return true
        diag.error(
            DslError.E408_MODEL_PREVIEW_PROP_TYPE_MISMATCH,
            prop.position,
            prop.name,
            expectedName,
            actual.displayName(),
        )
        return false
    }

    private fun knownType(expr: ExprAst): IrType = when (expr) {
        is ExprAst.StateRef -> stateTypes[expr.name] ?: IrType.AnyT
        is ExprAst.SessionRef -> sessionTypes[expr.name] ?: IrType.AnyT
        is ExprAst.PersistRef -> persistTypes[expr.name] ?: IrType.AnyT
        else -> TypeInferencer.inferExpr(expr)
    }

    private fun checkSourcePath(literal: ExprAst.Literal.StringLit) {
        val value = literal.value
        if (value.isBlank() || isAbsolute(value) || !value.endsWith(".bbmodel", ignoreCase = true)) {
            diag.error(DslError.E409_MODEL_PREVIEW_SOURCE_PATH_INVALID, literal.position, value)
        }
    }

    private fun checkTexturePath(literal: ExprAst.Literal.StringLit) {
        val value = literal.value
        val extension = value.substringAfterLast('.', missingDelimiterValue = "").lowercase()
        if (value.isBlank() || isAbsolute(value) || extension !in TEXTURE_EXTENSIONS) {
            diag.error(DslError.E410_MODEL_PREVIEW_TEXTURE_PATH_INVALID, literal.position, value)
        }
    }

    private fun checkScale(
        value: Double,
        position: top.wcpe.allincore.dsl.compiler.source.SourcePosition,
    ) {
        if (!value.isFinite() || value <= 0.0) {
            diag.error(DslError.E411_MODEL_PREVIEW_SCALE_INVALID, position, value)
        }
    }

    private fun checkRotation(
        name: String,
        value: Double,
        position: top.wcpe.allincore.dsl.compiler.source.SourcePosition,
    ) {
        if (!value.isFinite()) {
            diag.error(DslError.E412_MODEL_PREVIEW_ROTATION_INVALID, position, name, value)
        }
    }

    private fun isAbsolute(value: String): Boolean =
        value.startsWith('/') || value.startsWith('\\') || SCHEME_OR_DRIVE.containsMatchIn(value)

    private companion object {
        const val MODEL_PREVIEW_KIND = "modelPreview"
        val ALLOWED_PROPS: Set<String> = PropMetadata.getProps(MODEL_PREVIEW_KIND).mapTo(linkedSetOf()) { it.name }
        val TEXTURE_EXTENSIONS: Set<String> = setOf("png", "jpeg", "jpg", "webp")
        val SCHEME_OR_DRIVE = Regex("^[A-Za-z][A-Za-z0-9+.-]*:")
    }
}
